import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import ProfileHeader from "@/components/profile/ProfileHeader";
import EarnedBadges from "@/components/profile/EarnedBadges";
import IdeaCard from "@/components/ideas/IdeaCard";
import FollowProfileButton from "@/components/profile/FollowProfileButton";
import StreakChip from "@/components/profile/StreakChip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const PublicProfile = () => {
  const { userId = "" } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [ideas, setIdeas] = useState<any[] | null>(null);
  const [ideaCount, setIdeaCount] = useState<number>(0);
  const [supportingCount, setSupportingCount] = useState<number>(0);
  const [followerCount, setFollowerCount] = useState<number>(0);
  const [votedIds, setVotedIds] = useState<Set<string>>(new Set());

  const isOwnProfile = user?.id === userId;

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setNotFound(false);

    // Sensitive columns (bio, organization, role, location, is_pro) are restricted
    // to authenticated users at the database level. Anonymous visitors only get
    // the public-safe columns.
    const profileQuery = user
      ? supabase.from("profiles").select("*").eq("user_id", userId).maybeSingle()
      : supabase
          .from("profiles")
          .select("id,user_id,display_name,avatar_url,created_at,updated_at")
          .eq("user_id", userId)
          .maybeSingle();

    const [profileRes, ideasRes, votesCountRes, followerRes] = await Promise.all([
      profileQuery,
      supabase
        .from("ideas")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false }),
      supabase
        .from("idea_votes")
        .select("idea_id", { count: "exact", head: true })
        .eq("user_id", userId),
      supabase
        .from("profile_followers")
        .select("id", { count: "exact", head: true })
        .eq("following_user_id", userId),
    ]);

    if (!profileRes.data) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setProfile(profileRes.data);
    const ideaList = ideasRes.data ?? [];
    setIdeaCount(ideaList.length);
    setSupportingCount(votesCountRes.count ?? 0);
    setFollowerCount(followerRes.count ?? 0);

    // Attach the same author profile to every idea (always this user)
    const enriched = ideaList.map((i) => ({
      ...i,
      profiles: {
        display_name: profileRes.data?.display_name,
        avatar_url: profileRes.data?.avatar_url,
      },
    }));
    setIdeas(enriched);

    // Load current viewer's votes against these ideas to render correct vote state
    if (user && ideaList.length > 0) {
      const { data: votes } = await supabase
        .from("idea_votes")
        .select("idea_id")
        .eq("user_id", user.id)
        .in("idea_id", ideaList.map((i) => i.id));
      setVotedIds(new Set((votes ?? []).map((v) => v.idea_id)));
    } else {
      setVotedIds(new Set());
    }

    setLoading(false);
  }, [userId, user]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const name = profile?.display_name?.trim();
    document.title = name ? `${name} | emrkt.co` : "Shareholder profile | emrkt.co";
  }, [profile?.display_name]);

  const handleVote = async (ideaId: string, hasVoted: boolean) => {
    if (!user) return;
    if (hasVoted) {
      await supabase
        .from("idea_votes")
        .delete()
        .eq("user_id", user.id)
        .eq("idea_id", ideaId);
      setVotedIds((prev) => {
        const next = new Set(prev);
        next.delete(ideaId);
        return next;
      });
      setIdeas((prev) =>
        prev?.map((i) => (i.id === ideaId ? { ...i, vote_count: Math.max(0, i.vote_count - 1) } : i)) ?? null,
      );
    } else {
      await supabase.from("idea_votes").insert({ user_id: user.id, idea_id: ideaId });
      setVotedIds((prev) => new Set(prev).add(ideaId));
      setIdeas((prev) =>
        prev?.map((i) => (i.id === ideaId ? { ...i, vote_count: i.vote_count + 1 } : i)) ?? null,
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <main className="container mx-auto max-w-3xl px-6 pt-32 pb-16 text-center">
          <h1 className="font-display text-2xl font-bold text-foreground">Profile not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We couldn't find a shareholder with that link.
          </p>
          <Link to="/ideas" className="inline-block mt-6">
            <Button variant="outline">Browse projects</Button>
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="container mx-auto max-w-5xl px-6 pt-32 pb-16">
        <div className="flex flex-col gap-4">
          <ProfileHeader
            displayName={profile?.display_name ?? ""}
            avatarUrl={profile?.avatar_url}
            bio={profile?.bio}
            organization={profile?.organization}
            role={profile?.role}
            location={profile?.location}
            ideaCount={ideaCount}
            supportingCount={supportingCount}
            showEditButton={isOwnProfile}
            profile={profile}
          />
          <div className="flex flex-wrap items-center gap-3">
            <StreakChip userId={userId} />
            <span className="text-xs text-muted-foreground">
              <strong className="text-foreground">{followerCount}</strong> follower{followerCount === 1 ? "" : "s"}
            </span>
            {!isOwnProfile && (
              <FollowProfileButton
                targetUserId={userId}
                onChange={(now) =>
                  setFollowerCount((c) => Math.max(0, c + (now ? 1 : -1)))
                }
              />
            )}
          </div>
        </div>
        <EarnedBadges userId={userId} />

        <Tabs defaultValue="ideas" className="mt-8">
          <TabsList>
            <TabsTrigger value="ideas">
              Ideas ({ideas?.filter((i) => i.source_type !== "external").length ?? 0})
            </TabsTrigger>
            <TabsTrigger value="signals">
              External Signals ({ideas?.filter((i) => i.source_type === "external").length ?? 0})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="ideas" className="mt-6">
            {ideas === null ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-64 w-full rounded-2xl" />
                ))}
              </div>
            ) : ideas.filter((i) => i.source_type !== "external").length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                No original ideas yet.
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {ideas.filter((i) => i.source_type !== "external").map((idea) => (
                  <IdeaCard
                    key={idea.id}
                    idea={idea}
                    hasVoted={votedIds.has(idea.id)}
                    onVote={(hasVoted) => handleVote(idea.id, hasVoted)}
                    currentUserId={user?.id ?? null}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="signals" className="mt-6">
            {ideas && ideas.filter((i) => i.source_type === "external").length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                No external links shared yet.
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {ideas?.filter((i) => i.source_type === "external").map((idea) => (
                  <IdeaCard
                    key={idea.id}
                    idea={idea}
                    hasVoted={votedIds.has(idea.id)}
                    onVote={(hasVoted) => handleVote(idea.id, hasVoted)}
                    currentUserId={user?.id ?? null}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
      <SiteFooter />
    </div>
  );
};

export default PublicProfile;
